/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import AddIcon from '@mui/icons-material/Add'
import PersonIcon from '@mui/icons-material/Person'
import ComputerIcon from '@mui/icons-material/Computer'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { filterPlayers, nonDemoPlayers, addPlayer, settingsRemovePlayer, settingsUpdatePlayer, PLAYER_TYPE_COMPUTER } from '../../actions/game'
import PlayerDialog from './PlayerDialog'
import { LEVELS, log } from '../../logs'

const PlayerPicker = props => {
  log('PlayerPicker', LEVELS.INFO, props)
  const { players, maxPlayerCount, addClick, editClick, deleteClick, dispatch } = props
  const intl = useIntl()
  const players_ = filterPlayers(players, nonDemoPlayers)
  const [state, setState] = useState({ dialog: null, player: null })
  const { dialog, player } = state

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleCancel = useCallback(() => {
    setState({ dialog: null, player: null })
  }, [])

  const handleAdd = useCallback(player => {
    log('PlayerPicker', LEVELS.DEBUG, 'handleAdd', player)
    dispatch(addPlayer(player))
    setState({ dialog: null, player: null })
  }, [addClick])

  const handleEdit = useCallback(player => {
    log('PlayerPicker', LEVELS.DEBUG, 'handleEdit', player)
    dispatch(settingsUpdatePlayer(player))
    setState({ dialog: null, player: null })
  }, [editClick])

  const handleDelete = useCallback(() => {
    log('PlayerPicker', LEVELS.DEBUG, 'handleDelete', player)
    dispatch(settingsRemovePlayer(player.id))
    setState({ dialog: null, player: null })
  }, [deleteClick, player])

  const handleShowAdd = useCallback(() => {
    log('PlayerPicker', LEVELS.DEBUG, 'handleShowAdd')
    setState({ dialog: 'addPlayer', player: null })
  }, [])

  const handleShowEdit = useCallback(player => {
    log('PlayerPicker', LEVELS.DEBUG, 'handleShowEdit')
    setAnchorEl(null)
    setState({ dialog: 'editPlayer', player })
  }, [])

  const handleShowDelete = useCallback(player => {
    log('PlayerPicker', LEVELS.DEBUG, 'handleShowDelete')
    setAnchorEl(null)
    setState({ dialog: 'deletePlayer', player })
  }, [])

  return (
    <div style={{ width: '100%' }}>
      <List>
        {
        players_.map((player, index) =>
          <ListItem
            key={player.id}
            secondaryAction={
              <IconButton edge='end' onClick={handleMenuClick}>
                <MoreVertIcon />
              </IconButton>
            }
          >
            <ListItemAvatar>
              <Avatar>
                {player.type === PLAYER_TYPE_COMPUTER ? <ComputerIcon /> : <PersonIcon />}
              </Avatar>
            </ListItemAvatar>
            <ListItemText primary={player.name} />
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <MenuItem
                onClick={() => handleShowEdit(player)}
              >
                <ListItemIcon>
                  <EditIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>{intl.formatMessage({ id: 'common.modify' })}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => handleShowDelete(player)}
                disabled={players.length <= 1}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>{intl.formatMessage({ id: 'common.remove' })}</ListItemText>
              </MenuItem>
            </Menu>
          </ListItem>)
      }
        <Divider />
        <ListItem>
          <ListItemButton
            onClick={handleShowAdd}
            disabled={players_.length >= maxPlayerCount}
          >
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary={intl.formatMessage({ id: 'settings.addPlayer' })} />
          </ListItemButton>
        </ListItem>
      </List>
      {
        dialog === 'addPlayer'
          ? <PlayerDialog
              player={player}
              onClick={handleAdd}
              onCancel={handleCancel}
            />
          : null
      }
      {
        dialog === 'editPlayer'
          ? <PlayerDialog
              player={player}
              onClick={handleEdit}
              onCancel={handleCancel}
            />
          : null
      }
      <Dialog
        open={dialog === 'deletePlayer'}
        onClose={handleCancel}
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'common.remove' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{intl.formatMessage(
            { id: 'settings.removePlayerMessage' },
            { player: player ? player.name : 'n/a' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={handleDelete} autoFocus>
            {intl.formatMessage({ id: 'common.remove' })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
PlayerPicker.displayName = 'PlayerPicker'

const mapStateToProps = ({ players }) => ({ players })
export default connect(mapStateToProps)(PlayerPicker)
